import { Box, Grid, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { SxProps } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CustomButton, InfoCard } from "src/design";
import { RestaurantSummary } from "src/data/admin/types/organization";
import {
  colorAccent,
  colorBorder,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
} from "src/design/tokens";

interface UnsubscribedRestaurantsCardProps {
  /** The restaurants the organization holds that no longer carry a subscription. */
  restaurants: RestaurantSummary[];
  sx?: SxProps;
}

/**
 * The way back in for an owner who unsubscribed: the dashboard lists no
 * restaurant, but the organization still holds the ones below and their payment
 * history — and declaring a payment there is how a subscription is reinstated.
 */
export const UnsubscribedRestaurantsCard = ({
  restaurants,
  sx,
}: UnsubscribedRestaurantsCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <InfoCard
      title={t("noRestaurant.unsubscribed.title")}
      subtitle={t("noRestaurant.unsubscribed.message")}
      icon={<ReceiptLongOutlinedIcon sx={{ color: colorAccent }} />}
      sx={sx}
    >
      <Grid container spacing={1.5}>
        {restaurants.map((restaurant) => (
          <Grid item xs={12} key={restaurant.id}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                p: 2,
                border: `1px solid ${colorBorder}`,
                borderRadius: radiusCard,
              }}
            >
              <Box>
                <Typography
                  sx={{ fontWeight: 600, color: colorTextPrimary }}
                >
                  {restaurant.name}
                </Typography>
                <Typography variant="body2" sx={{ color: colorTextMuted }}>
                  {restaurant.city}
                </Typography>
              </Box>
              <CustomButton
                color="black"
                onClick={() =>
                  navigate(`/restaurant/${restaurant.id}/payments`)
                }
              >
                {t("noRestaurant.unsubscribed.viewPayments")}
              </CustomButton>
            </Box>
          </Grid>
        ))}
      </Grid>
    </InfoCard>
  );
};
